import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Navbar from "../navbar/Navbar";
import {
  getRepository,
  getCommitHistory,
  listTags,
  createTag,
  triggerImport,
} from "../../services/registry";
import API from "../../services/api.ts";
import { deleteModel, toggleModelPublic, removeModelFromMyModels } from "../../services/aiModels";
import "./ModelRegistry.css";
import "./RepositoryDetail.css";
import "../datasets/Datasets.css";
import "../shared/starfield.css";

const RepositoryDetail = () => {
  const { owner, name } = useParams();
  const navigate = useNavigate();

  const [repo, setRepo] = useState(null);
  const [commits, setCommits] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [showTagModal, setShowTagModal] = useState(false);
  const [newTagName, setNewTagName] = useState("");
  const [newTagMessage, setNewTagMessage] = useState("");

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      // Fetch repository details
      const repoResponse = await getRepository(owner, name);
      setRepo(repoResponse.data);

      // Fetch commit history
      try {
        const commitsResponse = await getCommitHistory(owner, name, 10);
        setCommits(commitsResponse.data?.commits || []);
      } catch (err) {
        console.warn("Could not fetch commits:", err);
      }

      // Fetch tags
      try {
        const tagsResponse = await listTags(owner, name);
        setTags(tagsResponse.data || []);
      } catch (err) {
        console.warn("Could not fetch tags:", err);
      }
    } catch (err) {
      console.error("Error fetching repository:", err);
      toast.error("Failed to load repository");
      navigate("/Registry");
    } finally {
      setLoading(false);
    }
  }, [owner, name, navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Poll for model build status if model is importing/building
  useEffect(() => {
    if (!repo?.ai_model) return;
    if (!["importing", "building", "pending"].includes(repo.ai_model.build_status)) return;

    const interval = setInterval(async () => {
      try {
        const repoResponse = await getRepository(owner, name);
        setRepo(repoResponse.data);

        if (
          repoResponse.data?.ai_model?.build_status === "ready" ||
          repoResponse.data?.ai_model?.build_status === "failed"
        ) {
          if (repoResponse.data.ai_model.build_status === "ready") {
            toast.success(`Model "${repoResponse.data.ai_model.name}" is ready!`);
          }
          clearInterval(interval);
        }
      } catch (err) {
        console.error("Error polling status:", err);
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [repo?.ai_model?.build_status, owner, name]);

  const handleTriggerImport = async () => {
    try {
      setImporting(true);
      await triggerImport(owner, name);
      toast.success("Import started! Build may take a few minutes.");
      fetchData();
    } catch (err) {
      console.error("Error triggering import:", err);
      toast.error(err.response?.data?.error || "Failed to trigger import");
    } finally {
      setImporting(false);
    }
  };

  const handleTogglePublic = async () => {
    if (!repo?.ai_model) return;
    try {
      const result = await toggleModelPublic(repo.ai_model.id);
      toast.success(result.message);
      fetchData();
    } catch (err) {
      console.error("Error toggling public status:", err);
      toast.error(err.response?.data?.error || "Failed to change public status");
    }
  };

  const handleDeleteModel = async () => {
    if (!repo?.ai_model) return;
    if (!window.confirm(`Delete model "${repo.ai_model.name}"? This will also delete the linked registry entry.`)) return;
    try {
      await deleteModel(repo.ai_model.id);
      toast.success("Model deleted");
      navigate("/Models");
    } catch (err) {
      console.error("Error deleting model:", err);
      toast.error(err.response?.data?.error || "Failed to delete model");
    }
  };

  const handleRemoveFromMyModels = async () => {
    if (!repo?.ai_model) return;
    if (!window.confirm(`Remove "${repo.ai_model.name}" from your models?`)) return;
    try {
      const result = await removeModelFromMyModels(repo.ai_model.id);
      toast.success(result.message);
      navigate("/Models");
    } catch (err) {
      console.error("Error removing model:", err);
      toast.error(err.response?.data?.error || "Failed to remove model");
    }
  };

  const handleImportFromRegistry = async () => {
    try {
      setImporting(true);
      const response = await API.post("/ai-models/models/import-from-registry/", {
        full_name: `${owner}/${name}`,
      });

      if (response.data.already_exists) {
        toast.info("Model already imported");
      } else {
        toast.success(`Model "${response.data.model.name}" import started!`);
      }
      fetchData();
    } catch (err) {
      console.error("Error importing model:", err);
      toast.error(err.response?.data?.error || "Failed to import model");
    } finally {
      setImporting(false);
    }
  };

  const handleCreateTag = async () => {
    if (!newTagName.trim()) {
      toast.error("Tag name is required");
      return;
    }

    try {
      await createTag(owner, name, {
        name: newTagName.trim(),
        message: newTagMessage.trim(),
      });
      toast.success(`Tag "${newTagName}" created`);
      setShowTagModal(false);
      setNewTagName("");
      setNewTagMessage("");
      fetchData();
    } catch (err) {
      console.error("Error creating tag:", err);
      toast.error(err.response?.data?.error || "Failed to create tag");
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString();
  };

  const formatRelativeTime = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const getBuildStatusBadge = (status) => {
    const statusConfig = {
      importing: { icon: "fa-spinner fa-spin", label: "Importing", class: "importing" },
      pending: { icon: "fa-clock", label: "Pending", class: "pending" },
      building: { icon: "fa-spinner fa-spin", label: "Building", class: "building" },
      ready: { icon: "fa-check", label: "Ready", class: "ready" },
      failed: { icon: "fa-times", label: "Failed", class: "failed" },
    };
    const config = statusConfig[status] || { icon: "fa-question", label: status, class: "unknown" };
    return (
      <span className={`build-status-badge ${config.class}`}>
        <i className={`fa ${config.icon}`}></i>
        {config.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="registry-page">
        <Navbar />
        <div className="container py-4">
          <div className="loading-state">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!repo) {
    return (
      <div className="registry-page">
        <Navbar />
        <div className="container py-4">
          <div className="empty-state">
            <i className="fa fa-exclamation-circle"></i>
            <p>Repository not found</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="registry-page datasets-page">
      <div className="starfield-background"></div>

      <Navbar />

      <div className="container py-4">
        {/* Back Button (matches Datasets detail page) */}
        <button
          className="back-button"
          onClick={() => navigate(repo.repo_type === 'tool' ? "/Tools" : repo.repo_type === 'dataset' ? "/Datasets" : repo.repo_type === 'study' ? "/Studies" : "/Models")}
        >
          <i className="fa fa-arrow-left"></i>
          {repo.repo_type === 'tool' ? 'Back to Tools'
            : repo.repo_type === 'dataset' ? 'Back to Datasets'
            : repo.repo_type === 'study' ? 'Back to Studies'
            : 'Back to Models'}
        </button>

        {/* Header */}
        <div className="repo-detail-header">
          <div className="repo-detail-header-left">
            <div className="repo-detail-icon">
              <i className={`fa ${repo.repo_type === 'dataset' ? 'fa-database' : repo.repo_type === 'study' ? 'fa-book' : 'fa-cube'}`}></i>
            </div>
            <div className="repo-detail-info">
              <h1 className="repo-detail-title">{repo.full_name}</h1>
              <p className="repo-detail-description">
                {repo.description || repo.ai_model?.description || "No description provided"}
              </p>
              <div className="repo-detail-meta">
                <span>
                  <i className="fa fa-user me-1"></i>
                  {repo.owner?.username}
                </span>
                {repo.model_type && (
                  <span>
                    <i className="fa fa-tags me-1"></i>
                    {repo.model_type}
                  </span>
                )}
                <span>
                  <i className="fa fa-calendar me-1"></i>
                  Created {formatRelativeTime(repo.created_at)}
                </span>
                {repo.is_public ? (
                  <span className="badge badge-public">
                    <i className="fa fa-globe me-1"></i>Public
                  </span>
                ) : (
                  <span className="badge badge-private">
                    <i className="fa fa-lock me-1"></i>Private
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="repo-detail-actions">
            {/* Sharing + delete (moved from /Models and /Tools tables).
                Applies to both models and tools since they both back onto
                DVAIModel via `repo.ai_model`. */}
            {(repo.repo_type === 'model' || repo.repo_type === 'tool' || !repo.repo_type) && repo.ai_model && (
              <>
                {repo.is_owner ? (
                  <>
                    <button
                      className={`btn-share ${repo.ai_model.is_public ? 'public' : 'private'}`}
                      onClick={handleTogglePublic}
                      title={repo.ai_model.is_public ? 'Click to make private' : 'Click to make public'}
                      disabled={repo.ai_model.build_status !== 'ready' && !repo.ai_model.is_public}
                    >
                      <i className={`fa ${repo.ai_model.is_public ? 'fa-globe' : 'fa-lock'} me-2`}></i>
                      {repo.ai_model.is_public ? 'Public' : 'Private'}
                    </button>
                    <button className="btn btn-outline-danger" onClick={handleDeleteModel}>
                      <i className="fa fa-trash me-2"></i>
                      Delete {repo.repo_type === 'tool' ? 'Tool' : 'Model'}
                    </button>
                  </>
                ) : repo.ai_model.is_added && (
                  <button className="btn btn-outline-danger" onClick={handleRemoveFromMyModels}>
                    <i className="fa fa-times me-2"></i>
                    Remove from My {repo.repo_type === 'tool' ? 'Tools' : 'Models'}
                  </button>
                )}
              </>
            )}
            {repo.is_owner && (repo.repo_type === 'model' || !repo.repo_type) && (
              <button className="btn-create-tag" onClick={() => setShowTagModal(true)}>
                <i className="fa fa-tag me-2"></i>
                Create Tag
              </button>
            )}
            {/* Model-specific actions */}
            {(repo.repo_type === 'model' || !repo.repo_type) && (
              repo.ai_model ? (
                <button className="btn-view-model" onClick={() => navigate("/Models")}>
                  <i className="fa fa-eye me-2"></i>
                  View Model
                </button>
              ) : (
                <button
                  className="btn-import-model"
                  onClick={handleImportFromRegistry}
                  disabled={importing}
                >
                  {importing ? (
                    <>
                      <i className="fa fa-spinner fa-spin me-2"></i>
                      Importing...
                    </>
                  ) : (
                    <>
                      <i className="fa fa-download me-2"></i>
                      Import Model
                    </>
                  )}
                </button>
              )
            )}
            {/* Dataset-specific actions */}
            {repo.repo_type === 'dataset' && (
              <button className="btn-view-model" onClick={() => navigate("/Datasets")}>
                <i className="fa fa-database me-2"></i>
                View Datasets
              </button>
            )}
            {/* Study-specific actions */}
            {repo.repo_type === 'study' && (
              <button className="btn-view-model" onClick={() => navigate("/Studies")}>
                <i className="fa fa-book me-2"></i>
                View Studies
              </button>
            )}
          </div>
        </div>

        {/* Model Status Card (if imported) - Only show for model repos */}
        {repo.ai_model && (repo.repo_type === 'model' || !repo.repo_type) && (
          <div className="repo-model-card">
            <div className="repo-model-header">
              <i className="fa fa-brain me-2"></i>
              <span>Linked AI Model</span>
            </div>
            <div className="repo-model-body">
              <div className="repo-model-info">
                <div className="repo-model-name">{repo.ai_model.name}</div>
                <div className="repo-model-status">
                  {getBuildStatusBadge(repo.ai_model.build_status)}
                </div>
              </div>
              {repo.ai_model.build_error && (
                <div className="repo-model-error">
                  <i className="fa fa-exclamation-triangle me-2"></i>
                  {repo.ai_model.build_error}
                </div>
              )}
              <div className="repo-model-actions">
                <button
                  className="btn-rebuild"
                  onClick={handleTriggerImport}
                  disabled={importing || repo.ai_model.build_status === "building"}
                >
                  <i className="fa fa-sync me-2"></i>
                  Rebuild
                </button>
                <button className="btn-view-model-small" onClick={() => navigate("/Models")}>
                  <i className="fa fa-external-link-alt me-2"></i>
                  Open in Models
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Two Column Layout */}
        <div className="repo-detail-grid">
          {/* Commits Section */}
          <div className="repo-section">
            <div className="repo-section-header">
              <i className="fa fa-history me-2"></i>
              <span>Recent Commits</span>
              <span className="section-count">{commits.length}</span>
            </div>
            <div className="repo-section-body">
              {commits.length === 0 ? (
                <div className="empty-section">
                  <i className="fa fa-code-commit"></i>
                  <p>No commits yet</p>
                </div>
              ) : (
                <div className="commit-list">
                  {commits.map((commit) => (
                    <div key={commit.sha} className="commit-item">
                      <div className="commit-sha">
                        <i className="fa fa-code-commit me-1"></i>
                        {commit.short_sha}
                      </div>
                      <div className="commit-message">{commit.message}</div>
                      <div className="commit-meta">
                        <span>
                          <i className="fa fa-user me-1"></i>
                          {commit.author_name}
                        </span>
                        <span>
                          <i className="fa fa-clock me-1"></i>
                          {formatRelativeTime(commit.timestamp)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Tags Section */}
          <div className="repo-section">
            <div className="repo-section-header">
              <i className="fa fa-tags me-2"></i>
              <span>Tags / Versions</span>
              <span className="section-count">{tags.length}</span>
            </div>
            <div className="repo-section-body">
              {tags.length === 0 ? (
                <div className="empty-section">
                  <i className="fa fa-tag"></i>
                  <p>No tags yet</p>
                  {repo.is_owner && (
                    <button
                      className="btn-create-tag-small"
                      onClick={() => setShowTagModal(true)}
                    >
                      <i className="fa fa-plus me-2"></i>
                      Create Tag
                    </button>
                  )}
                </div>
              ) : (
                <div className="tag-list">
                  {tags.map((tag) => (
                    <div key={tag.id} className="tag-item">
                      <div className="tag-name">
                        <i className="fa fa-tag me-2"></i>
                        {tag.name}
                      </div>
                      {tag.message && <div className="tag-message">{tag.message}</div>}
                      <div className="tag-meta">
                        <span className="tag-sha">{tag.commit_sha?.substring(0, 7)}</span>
                        <span>{formatRelativeTime(tag.created_at)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Clone/Pull Instructions - Different for each repo type */}
        <div className="repo-clone-card">
          <div className="repo-clone-header">
            <i className="fa fa-terminal me-2"></i>
            {repo.repo_type === 'model' || !repo.repo_type ? 'Clone this repository' : `Pull this ${repo.repo_type}`}
          </div>
          <div className="repo-clone-body">
            {repo.repo_type === 'dataset' ? (
              <code className="clone-url">
                dv datasets pull {owner}/{name}
              </code>
            ) : repo.repo_type === 'study' ? (
              <code className="clone-url">
                dv studies pull {owner}/{name}
              </code>
            ) : (
              <code className="clone-url">
                git clone {window.location.origin}/api/registry/{owner}/{name}.git
              </code>
            )}
            <button
              className="btn-copy"
              onClick={() => {
                let command;
                if (repo.repo_type === 'dataset') {
                  command = `dv datasets pull ${owner}/${name}`;
                } else if (repo.repo_type === 'study') {
                  command = `dv studies pull ${owner}/${name}`;
                } else {
                  command = `git clone ${window.location.origin}/api/registry/${owner}/${name}.git`;
                }
                navigator.clipboard.writeText(command);
                toast.success("Command copied!");
              }}
            >
              <i className="fa fa-copy"></i>
            </button>
          </div>
        </div>
      </div>

      {/* Create Tag Modal */}
      {showTagModal && (
        <div className="modal-overlay" onClick={() => setShowTagModal(false)}>
          <div className="tag-modal" onClick={(e) => e.stopPropagation()}>
            <div className="tag-modal-header">
              <i className="fa fa-tag me-2"></i>
              Create New Tag
              <button className="modal-close" onClick={() => setShowTagModal(false)}>
                <i className="fa fa-times"></i>
              </button>
            </div>
            <div className="tag-modal-body">
              <div className="form-group">
                <label>Tag Name</label>
                <input
                  type="text"
                  placeholder="e.g., v1.0.0"
                  value={newTagName}
                  onChange={(e) => setNewTagName(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>Release Notes (optional)</label>
                <textarea
                  placeholder="Describe this release..."
                  value={newTagMessage}
                  onChange={(e) => setNewTagMessage(e.target.value)}
                  rows={3}
                />
              </div>
            </div>
            <div className="tag-modal-footer">
              <button className="btn-cancel" onClick={() => setShowTagModal(false)}>
                Cancel
              </button>
              <button className="btn-create" onClick={handleCreateTag}>
                <i className="fa fa-tag me-2"></i>
                Create Tag
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RepositoryDetail;
